/*
 * MUTE Search Drawer — Bridge JS (Phase 1 Task 1.7, 2026-05-23)
 * Wires [data-mute-search-trigger] → open MuteSearchDrawer
 * Handles ESC, backdrop-click, close-button, focus-trap, body-scroll-lock.
 * Compatible with Dawn predictive-search.js (no conflict — opens before, closes after).
 */
(function () {
  'use strict';

  const DRAWER_ID = 'MuteSearchDrawer';
  const HTML_OPEN_CLASS = 'mute-search-open';
  const STATE_OPEN = 'false';

  let drawer, panel, input, backdrop, closeBtn, triggers, lastFocused;

  function init() {
    drawer = document.getElementById(DRAWER_ID);
    if (!drawer) {
      console.warn('[mute-search] Drawer #' + DRAWER_ID + ' not found in DOM — snippet not rendered?');
      return;
    }
    panel = drawer.querySelector('.mute-search-drawer__panel');
    input = drawer.querySelector('#MuteSearchInput');
    backdrop = drawer.querySelector('[data-mute-search-backdrop]');
    closeBtn = drawer.querySelector('[data-mute-search-close]');
    triggers = document.querySelectorAll('[data-mute-search-trigger]');

    triggers.forEach((t) => t.addEventListener('click', onTriggerClick));
    if (backdrop) backdrop.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', onKeydown);

    // Submit fallback: when user presses Enter in an empty input, do nothing.
    // Dawn predictive-search already handles non-empty submit by navigating to /search.
  }

  function onTriggerClick(e) {
    e.preventDefault();
    open(e.currentTarget);
  }

  function open(trigger) {
    if (!drawer) return;
    lastFocused = trigger || document.activeElement;
    drawer.hidden = false;
    drawer.removeAttribute('inert');
    // Force a reflow so transition can pick up
    void drawer.offsetWidth;
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add(HTML_OPEN_CLASS);
    triggers.forEach((t) => t.setAttribute('aria-expanded', 'true'));
    // Focus input shortly after — give animation a moment
    setTimeout(() => {
      if (input) input.focus({ preventScroll: true });
    }, 80);
    document.dispatchEvent(new CustomEvent('mute:search:open'));
  }

  function close() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    document.documentElement.classList.remove(HTML_OPEN_CLASS);
    triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    // Hide after transition to truly remove from a11y tree
    setTimeout(() => {
      if (drawer.getAttribute('aria-hidden') === 'true') drawer.hidden = true;
    }, 220);
    // Return focus to trigger that opened the drawer
    if (lastFocused && typeof lastFocused.focus === 'function') {
      try { lastFocused.focus({ preventScroll: true }); } catch (_) { /* noop */ }
    }
    document.dispatchEvent(new CustomEvent('mute:search:close'));
  }

  function onKeydown(e) {
    if (!drawer || drawer.getAttribute('aria-hidden') !== 'false') return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') {
      trapFocus(e);
    }
  }

  function trapFocus(e) {
    const focusables = panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Expose minimal API for other modules (e.g., quick-search keyboard shortcut)
  window.MuteSearchDrawer = { open, close };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
